import axios, { AxiosResponse } from 'axios';
import { ApiResponse } from '../types';
import config from '../config.env';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: config?.API_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies in requests
});

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    
    // Handle 401 errors
    if (error.response?.status === 401) {
      // Session expired - handled by auth context
    }
    
    return Promise.reject(error);
  }
);

// API Service
export const apiService = {
  async getUsers(): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await apiClient.get('/users');
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to get users');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || error.message);
      }
      throw error;
    }
  },

  async createUser(userData: any): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await apiClient.post('/users', userData);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to create user');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || error.message);
      }
      throw error;
    }
  },

  async getHealthStatus(): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await apiClient.get('/health');
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to get health status');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || error.message);
      }
      throw error;
    }
  },

  // Assets
  async getAssets(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await apiClient.get('/assets');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to get assets');
  },

  async createAsset(assetData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await apiClient.post('/assets', assetData);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to create asset');
  },

  async getAsset(id: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await apiClient.get(`/assets/${id}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to get asset');
  },

  async registerContract(assetId: string, contractAddress: string, chainId?: number): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await apiClient.post(
      `/assets/${assetId}/register-contract`,
      { contractAddress, chainId }
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to register contract');
  },

  // Wallets
  async connectWallet(address: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await apiClient.post('/wallets/connect', { address });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to connect wallet');
  },

  async getWallets(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await apiClient.get('/wallets');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to get wallets');
  },

  // Vesting
  async getVestingGrants(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await apiClient.get('/vesting/grants');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to get vesting grants');
  },

  async createVestingGrant(grantData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await apiClient.post('/vesting/grants', grantData);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to create vesting grant');
  },

  // Distributions
  async getDistributions(assetId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await apiClient.get(`/assets/${assetId}/distributions`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to get distributions');
  },

  async getDistributionEntitlements(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await apiClient.get('/distributions/entitlements');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to get distribution entitlements');
  },

  async declareDistribution(assetId: string, distributionData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await apiClient.post(
      `/assets/${assetId}/distributions`,
      distributionData
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to declare distribution');
  },
};

export default apiService;
